import styled from "styled-components";

import { ChevronRight, SWReactIcons } from "@similarweb/icons";
import { colorsPalettes, setFontVariant } from "@similarweb/styles";

import type { TopicsRawData } from "app/pages/market-research/api-client/demandSearchTrendsTopicsAPI";

type ActiveProps = {
    isActive: boolean;
};

const CATEGORY_SIDEBAR_WIDTH = 320;

const CategoryItemName = styled.span<ActiveProps>`
    ${setFontVariant("primaryItem", colorsPalettes.carbon[500])};
    flex: 1;
    min-width: 0;
    text-transform: capitalize;
    color: ${({ isActive }) => (isActive ? colorsPalettes.blue[400] : colorsPalettes.carbon[500])};
`;

const CategoryItemIcon = styled(SWReactIcons)<ActiveProps>`
    width: 24px;
    height: 24px;
    flex-shrink: 0;

    path {
        fill: ${({ isActive }) => (isActive ? colorsPalettes.blue[400] : colorsPalettes.carbon[200])};
    }
`;

const ChevronWrapper = styled.span<ActiveProps>`
    display: inline-flex;
    flex-shrink: 0;
    color: ${({ isActive }) => (isActive ? colorsPalettes.blue[400] : colorsPalettes.carbon[200])};
`;

const CategoryItem = styled.div<ActiveProps>`
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    padding: 12px 4px 12px 12px;
    border-bottom: 1px solid ${colorsPalettes.carbon[50]};
    background-color: ${({ isActive }) => (isActive ? colorsPalettes.blue[50] : colorsPalettes.carbon[0])};

    &:hover {
        background-color: ${colorsPalettes.blue[50]};

        ${CategoryItemName} {
            color: ${colorsPalettes.blue[400]};
        }

        ${CategoryItemIcon} path {
            fill: ${colorsPalettes.blue[400]};
        }

        ${ChevronWrapper} {
            color: ${colorsPalettes.blue[400]};
        }
    }
`;

const ListContainer = styled.div`
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    width: ${CATEGORY_SIDEBAR_WIDTH}px;
    min-width: ${CATEGORY_SIDEBAR_WIDTH}px;
    height: 100%;
    border-right: 1px solid ${colorsPalettes.carbon[50]};
    scrollbar-width: thin;
`;

type TopicsHeatmapCategoryListProps = {
    data: TopicsRawData;
    activeCategory: string;
    onCategorySelect: (categoryName: string) => void;
};

export const TopicsHeatmapCategoryList = ({
    data,
    activeCategory,
    onCategorySelect,
}: TopicsHeatmapCategoryListProps) => (
    <ListContainer>
        {Object.entries(data).map(([categoryName, category]) => {
            const isActive = categoryName === activeCategory;

            return (
                <CategoryItem
                    key={categoryName}
                    isActive={isActive}
                    onClick={() => onCategorySelect(categoryName)}
                    data-automation={`heatmap-category-${categoryName}`}
                >
                    <CategoryItemIcon
                        iconName={category?.Favicon}
                        isActive={isActive}
                    />
                    <CategoryItemName isActive={isActive}>{category.Name}</CategoryItemName>
                    <ChevronWrapper isActive={isActive}>
                        <ChevronRight size="sm" />
                    </ChevronWrapper>
                </CategoryItem>
            );
        })}
    </ListContainer>
);
